import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IFolderSource } from '@components/ui-folder-tree/ui-folder-tree-state';
import {
    IFolder,
    IFolderAssignment,
    IFolderContext,
    IRole,
    IUser,
    IUserRoles,
    ODataResponse,
    UserType,
} from '@models';
import {
    ODataComparisonProperty,
    ODataQueryOptions,
} from '@services';
import { addToObjectIf } from '@utils/object.utils';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ODataService } from './generics/odata.service';
import { MapperService } from './mapper.service';

interface IADEntity {
    Domain: string;
    UserName: string;
    UserType: UserType;
}

@Injectable({
    providedIn: 'root',
})
export class FoldersService extends ODataService<IFolder, number> implements IFolderSource {
    public hasSwitchCapability = true;
    protected _endpoint = '/odata/Folders';

    constructor(http: HttpClient) {
        super(http, MapperService.foldersMap);
    }

    public getFolderContext(folderId: number | null, take: number, relativeSkip: number): Observable<IFolderContext> {
        return this._http.get<any>(
            `api/FoldersNavigation/GetFolderContext?relativeSkip=${relativeSkip}&take=${take}` +
            (
                folderId != null
                    ? `&folderId=${folderId}`
                    : ''
            ),
        )
            .pipe(
                map(r => r as {
                    AncestorIds: number[],
                    IndexInAllFoldersList: number,
                    Count: number,
                    PageItems: any[],
                }),
                map(({ AncestorIds, IndexInAllFoldersList, Count, PageItems }) => ({
                    ancestors: (AncestorIds || []),
                    index: IndexInAllFoldersList,
                    folders: (PageItems || []).map(item => this._mapData(item) as IFolder),
                    count: Count,
                })),
            );
    }

    public getFolders(expandedIds: number[], take: number, skip: number) {
        return this._http.get<any>(
            `api/FoldersNavigation/GetFoldersPageForCurrentUser?skip=${skip}&take=${take}` +
            (
                expandedIds.length
                    ? '&' + expandedIds.map(id => `expandedParentIds=${id}`).join('&')
                    : ''
            ),
        )
            .pipe(
                map(({ PageItems, Count }) =>
                    ({
                        data: (PageItems || []).map((item: any) => this._mapData(item) as IFolder),
                        total: Count,
                    }),
                ),
            );
    }

    public getQueryString(queryOptions?: ODataQueryOptions): string {
        // Extract ParentId from filters and add it as a separate query param.

        const filters = queryOptions &&
            queryOptions.filter &&
            (queryOptions.filter as ODataComparisonProperty).and &&
            (queryOptions.filter as ODataComparisonProperty).and instanceof Array &&
            (queryOptions.filter as ODataComparisonProperty).and;

        const parentIdFilterIndex = filters &&
            filters.findIndex(filter => filter.hasOwnProperty('ParentId'));

        let parentId = null;

        if (
            filters &&
            parentIdFilterIndex !== -1
        ) {
            parentId = (filters[parentIdFilterIndex as number] as any)
                .ParentId.eq;
        }

        let queryString = super.getQueryString(queryOptions);

        if (parentId !== null) {
            queryString = queryString.indexOf('?') !== -1 ?
                `${queryString}&parentId=${parentId}` :
                `${queryString}?parentId=${parentId}`;
        }

        return queryString;
    }

    public searchFolders(skip: number, take: number, searchText: string) {
        return this._http.get<any>(
            `api/FoldersNavigation/GetFoldersForCurrentUser?skip=${skip}&take=${take}&searchText=${encodeURIComponent(searchText)}`,
        )
            .pipe(
                map(({ PageItems, Count }) =>
                    ({
                        data: (PageItems || []).map((item: any) => this._mapData(item) as IFolder),
                        total: Count,
                    }),
                ),
            );
    }

    public addUserToFolder(userId: Array<IUser['id']>, folderId: IFolder['id'], roleIds?: IRole['id']) {
        const payload = {
            assignments: {
                UserIds: [userId],
                RolesPerFolder: [{
                    FolderId: folderId,
                    ...addToObjectIf<any>(
                        !!roleIds,
                        {
                            RoleIds: roleIds,
                        },
                    ),
                }],
            },
        };

        return this._http.post(
            `${this._endpoint}/UiPath.Server.Configuration.OData.AssignUsers`,
            JSON.stringify(payload),
        );
    }

    public addAdEntityToFolder = (entity: IADEntity, folderId: IFolder['id'], roleIds?: IRole['id']) => {
        const payload = {
            assignment: {
                ...entity,
                UserType: UserType[entity.UserType],
                RolesPerFolder: [{
                    FolderId: folderId,
                    ...addToObjectIf<any>(
                        !!roleIds,
                        {
                            RoleIds: roleIds,
                        },
                    ),
                }],
            },
        };

        return this._http.post(
            `${this._endpoint}/UiPath.Server.Configuration.OData.AssignDomainUser`,
            JSON.stringify(payload),
        );
    }

    public removeUserFromFolder(folderId: IFolder['id'], userId: IUser['id']) {
        const payload = { userId };

        return this._http.post(
            `${this._endpoint}(${folderId})/UiPath.Server.Configuration.OData.RemoveUserFromFolder`,
            JSON.stringify(payload),
        );
    }

    public checkAssignedUser(folderId: IFolder['id'], username: string) {
        const queryOptions: ODataQueryOptions = {
            top: 1,
            filter: {
                'UserEntity/UserName': username,
            },
        };

        return this._http.get<any>(
            `${this._endpoint}/UiPath.Server.Configuration.OData.GetUsersForFolder(key=${folderId},includeInherited=false)` +
            this.getQueryString(queryOptions),
        ).pipe(
            map(res => this._mapData(res, MapperService.folderAssignmentsMap) as ODataResponse<IFolderAssignment>),
            map(res => res.data[0]),
        );
    }

    public getAssignedUsers(folderId: IFolder['id'], includeInheritedUsers: boolean, queryOptions?: ODataQueryOptions) {
        return this._http.get<any>(
            `${this._endpoint}/UiPath.Server.Configuration.OData.GetUsersForFolder` +
            `(key=${folderId},includeInherited=${includeInheritedUsers})` +
            this.getQueryString(queryOptions),
        ).pipe(
            map(res => this._mapData(res, MapperService.folderAssignmentsMap)),
        ) as Observable<ODataResponse<IFolderAssignment>>;
    }

    public getAllRolesForUser = ({ username, skip = 0, take = 10 }: { username: string, skip: number, take: number }) =>
        this._http.get<any>(
            `api/FoldersNavigation/GetAllRolesForUser?username=${encodeURIComponent(username)}&skip=${skip}&take=${take}`,
        ).pipe(
            map(res => this._toDto(res, MapperService.userRolesMap) as unknown as IUserRoles),
        )
}
